/**
 * Blurp.ie AI Chat Service
 * Uses OpenAI API for intelligent responses with environmental context
 * Falls back to rule-based system if no API key
 */

import { useAirQualityStore } from '@/stores/useAirQualityStore';
import { useLocationStore } from '@/stores/useLocationStore';
import { useNavigationStore } from '@/stores/useNavigationStore';
import { useRunningStore } from '@/stores/useRunningStore';

// Global event for chat actions (open pages, etc.)
export function emitChatAction(action: string) {
  window.dispatchEvent(new CustomEvent('blurp-chat-action', { detail: action }));
}

interface ChatContext {
  neuralIndex: number;
  cognitiveDebt: number;
  molecularPermission: string;
  pm25: number | null;
  ozone: number | null;
  co2: number | null;
  aqi: number | null;
  isNavigating: boolean;
  destination: string | null;
  currentLocation: { lat: number; lng: number } | null;
  // Route details
  routeDistance: number | null; // meters
  routeDuration: number | null; // seconds
  routeETA: Date | null;
  routeAverageNI: number | null;
}

/**
 * Get current environmental context from stores
 */
function getContext(): ChatContext {
  const aqState = useAirQualityStore.getState();
  const locState = useLocationStore.getState();
  const navState = useNavigationStore.getState();

  const activeRoute = navState.activeRoute;

  return {
    neuralIndex: aqState.neuralIndex,
    cognitiveDebt: aqState.cognitiveDebt,
    molecularPermission: aqState.molecularPermission,
    pm25: aqState.pm25?.value ?? null,
    ozone: aqState.ozone?.value ?? null,
    co2: aqState.co2?.value ?? null,
    aqi: aqState.currentReading?.aqi ?? null,
    isNavigating: navState.isNavigating,
    destination: navState.selectedDestination?.name || activeRoute?.destinationName || null,
    currentLocation: locState.currentPosition,
    // Route details
    routeDistance: activeRoute?.totalDistance ?? null,
    routeDuration: activeRoute?.totalDuration ?? null,
    routeETA: navState.estimatedArrival,
    routeAverageNI: activeRoute?.averageNI ?? null,
  };
}

/**
 * Format duration in human readable format
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} seconds`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMins > 0 ? `${remainingMins} min` : ''}`.trim();
}

/**
 * Format distance in human readable format
 */
function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} meters`;
  const km = meters / 1000;
  return `${km.toFixed(1)} km`;
}

/**
 * Analyze user intent from message
 */
function analyzeIntent(message: string): string {
  const lower = message.toLowerCase();

  // Greetings
  if (/^(hi|hello|hey|greetings)/i.test(lower)) return 'greeting';

  // Time/Duration questions - HIGH PRIORITY (check before general navigation)
  if (/how long|how much time|time.*(take|reach|get|arrive)|eta|when.*(arrive|reach|get there)|minutes?.*(left|remaining|to go)|arrival time/i.test(lower)) return 'time_to_destination';

  // Distance questions
  if (/how far|distance|how many (km|kilometers|metres|meters|miles)|remaining distance/i.test(lower)) return 'distance_to_destination';

  // Air quality questions
  if (/air quality|aqi|pollution|pollut/i.test(lower)) return 'air_quality';
  if (/pm2\.?5|particulate/i.test(lower)) return 'pm25';
  if (/ozone|o3/i.test(lower)) return 'ozone';
  if (/co2|carbon dioxide/i.test(lower)) return 'co2';

  // Neural index questions
  if (/neural index|ni\b|brain|cognitive|focus|mental/i.test(lower)) return 'neural_index';
  if (/cognitive debt|productivity loss/i.test(lower)) return 'cognitive_debt';
  if (/molecular permission|permission/i.test(lower)) return 'molecular_permission';

  // Navigation questions
  if (/navigate|direction|route|how (do i |to )?(get|go)/i.test(lower)) return 'navigation';
  if (/where am i|my location|current location/i.test(lower)) return 'location';
  if (/safe.*(route|path|way)|best.*(route|path|way)/i.test(lower)) return 'safe_route';
  if (/start|begin|let'?s go|walking|started/i.test(lower) && /navigat|route|walk/i.test(lower)) return 'start_navigation';

  // Map questions
  if (/map|see anything|can't see|cant see|nothing.*map/i.test(lower)) return 'map_help';

  // Recommendations
  if (/should i|recommend|suggestion|what.*do|advice/i.test(lower)) return 'recommendation';
  if (/go outside|outdoor|walk|exercise/i.test(lower)) return 'outdoor_advice';

  // Weather/forecast
  if (/forecast|later|next hour|prediction|will.*be/i.test(lower)) return 'forecast';

  // About/help
  if (/what (is|are) (you|blurp)|who are you|help|how.*work/i.test(lower)) return 'about';

  // Thanks
  if (/thank|thanks/i.test(lower)) return 'thanks';

  // Yes/No responses
  if (/^(yes|yeah|yep|sure|ok|okay)$/i.test(lower.trim())) return 'affirmative';
  if (/^(no|nope|nah)$/i.test(lower.trim())) return 'negative';

  // Default
  return 'general';
}

/**
 * Generate response based on intent and context
 */
export function generateResponse(userMessage: string): string {
  const ctx = getContext();
  const intent = analyzeIntent(userMessage);

  switch (intent) {
    case 'greeting':
      return `Hello! I'm Blurp.ie, your cognitive navigation assistant. Your current Neural Index is ${ctx.neuralIndex}. How can I help you optimize your environment today?`;

    case 'time_to_destination':
      if (ctx.isNavigating && ctx.routeDuration !== null && ctx.destination) {
        const timeLeft = formatDuration(ctx.routeDuration);
        const eta = ctx.routeETA ? new Date(ctx.routeETA).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'soon';
        return `You'll reach ${ctx.destination} in approximately ${timeLeft}. Estimated arrival: ${eta}. The route maintains an average Neural Index of ${ctx.routeAverageNI ?? ctx.neuralIndex}, so you'll stay mentally sharp along the way!`;
      }
      if (ctx.destination) {
        return `You've selected ${ctx.destination} as your destination. Start navigation to get real-time walking time and ETA.`;
      }
      return `You haven't selected a destination yet. Use the search bar to find where you want to go, and I'll tell you how long it will take!`;

    case 'distance_to_destination':
      if (ctx.isNavigating && ctx.routeDistance !== null && ctx.destination) {
        return `You have ${formatDistance(ctx.routeDistance)} remaining to ${ctx.destination}. ${ctx.routeDistance < 500 ? "You're almost there!" : ctx.routeDistance < 1000 ? "Getting closer!" : "Keep going, you've got this!"}`;
      }
      if (ctx.destination) {
        return `${ctx.destination} is your selected destination. Start navigation to see the exact distance and route.`;
      }
      return `No destination selected yet. Search for a place to see the distance!`;

    case 'start_navigation':
      if (ctx.isNavigating) {
        return `You're already navigating to ${ctx.destination}! ${formatDistance(ctx.routeDistance ?? 0)} remaining, about ${formatDuration(ctx.routeDuration ?? 0)} to go.`;
      }
      if (ctx.destination) {
        return `Ready to go to ${ctx.destination}! Tap "Start Walk" to begin navigation. I'll guide you through the safest air quality route.`;
      }
      return `Search for a destination first, then tap "Start Walk" to begin!`;

    case 'affirmative':
      if (ctx.isNavigating) {
        return `Great! Keep following the route to ${ctx.destination}. ${formatDuration(ctx.routeDuration ?? 0)} remaining.`;
      }
      return `Sounds good! What would you like to do? I can help with navigation, air quality info, or recommendations.`;

    case 'negative':
      return `No problem! Let me know if you need anything else. I can help with air quality, navigation, or finding safe routes.`;

    case 'air_quality':
      if (ctx.aqi !== null) {
        const quality = ctx.aqi <= 50 ? 'good' : ctx.aqi <= 100 ? 'moderate' : 'poor';
        return `Current air quality index (AQI) is ${ctx.aqi}, which is ${quality}. PM2.5: ${ctx.pm25 ?? 'N/A'} µg/m³, Ozone: ${ctx.ozone ?? 'N/A'} ppb, CO2: ${ctx.co2 ?? 'N/A'} ppm. ${quality === 'good' ? 'Great conditions for outdoor activities!' : quality === 'moderate' ? 'Consider limiting prolonged outdoor exposure.' : 'Recommend staying indoors if possible.'}`;
      }
      return `I'm currently reading PM2.5 at ${ctx.pm25 ?? 'N/A'} µg/m³, Ozone at ${ctx.ozone ?? 'N/A'} ppb, and CO2 at ${ctx.co2 ?? 'N/A'} ppm. Your Neural Index is ${ctx.neuralIndex}, indicating ${ctx.neuralIndex >= 80 ? 'optimal' : ctx.neuralIndex >= 60 ? 'moderate' : 'suboptimal'} cognitive conditions.`;

    case 'pm25':
      const pm25Status = ctx.pm25 !== null
        ? ctx.pm25 <= 12 ? 'excellent (WHO safe)' : ctx.pm25 <= 35 ? 'moderate' : 'elevated - caution advised'
        : 'unavailable';
      return `PM2.5 is currently ${ctx.pm25 ?? 'N/A'} µg/m³ - ${pm25Status}. Fine particulate matter directly impacts cognitive function. ${ctx.pm25 && ctx.pm25 > 35 ? 'Consider using the safe route feature to avoid high-pollution areas.' : ''}`;

    case 'ozone':
      return `Ground-level ozone is at ${ctx.ozone ?? 'N/A'} ppb. ${ctx.ozone && ctx.ozone > 70 ? 'Ozone levels are elevated. Best to avoid strenuous outdoor activities.' : 'Ozone levels are within acceptable range.'}`;

    case 'co2':
      return `CO2 concentration is ${ctx.co2 ?? 'N/A'} ppm. ${ctx.co2 && ctx.co2 > 1000 ? 'Elevated CO2 may cause drowsiness and reduced concentration.' : 'CO2 levels are normal for outdoor air.'}`;

    case 'neural_index':
      const niLevel = ctx.neuralIndex >= 80 ? 'optimal' : ctx.neuralIndex >= 60 ? 'moderate' : 'compromised';
      return `Your Neural Index is ${ctx.neuralIndex}/100 (${niLevel}). This measures your predicted cognitive capacity based on current environmental conditions. ${ctx.neuralIndex >= 80 ? 'Excellent conditions for focused work!' : ctx.neuralIndex >= 60 ? 'Acceptable conditions, but performance may vary.' : 'Consider relocating to a cleaner environment for optimal focus.'}`;

    case 'cognitive_debt':
      return `Cognitive Debt is at ${ctx.cognitiveDebt}%. This represents accumulated productivity loss from environmental exposure. ${ctx.cognitiveDebt > 10 ? 'You may want to take a break in a cleaner area to recover.' : 'Your cognitive debt is minimal - keep it up!'}`;

    case 'molecular_permission':
      const permMsg = ctx.molecularPermission === 'GRANTED'
        ? 'GRANTED - conditions are optimal for outdoor activities and focused work.'
        : ctx.molecularPermission === 'CAUTION'
        ? 'CAUTION - some environmental factors may impact performance. Monitor your state.'
        : 'RESTRICTED - environmental conditions are not ideal. Consider indoor alternatives.';
      return `Molecular Permission status: ${permMsg}`;

    case 'navigation':
      if (ctx.isNavigating && ctx.destination) {
        const timeLeft = formatDuration(ctx.routeDuration ?? 0);
        const distLeft = formatDistance(ctx.routeDistance ?? 0);
        return `You're navigating to ${ctx.destination}!\n\n📍 Distance: ${distLeft}\n⏱️ Time: ${timeLeft}\n🧠 Route NI: ${ctx.routeAverageNI ?? ctx.neuralIndex}\n\nFollow the green path on the map to avoid pollution hotspots. I've optimized for the best air quality!`;
      }
      if (ctx.destination) {
        return `Great, you've selected ${ctx.destination}! Tap "Start Walk" to begin navigation. I'll guide you through the safest route.`;
      }
      return `To navigate, use the search bar at the top to find a destination. I'll calculate the safest route based on real-time air quality, prioritizing paths that keep your Neural Index high!`;

    case 'location':
      if (ctx.currentLocation) {
        return `You're currently at coordinates ${ctx.currentLocation.lat.toFixed(4)}°N, ${Math.abs(ctx.currentLocation.lng).toFixed(4)}°W. Neural Index at this location: ${ctx.neuralIndex}.`;
      }
      return `I don't have your exact location yet. The app is running in demo mode with a simulated San Francisco location.`;

    case 'safe_route':
      return `Safe routes are calculated by analyzing real-time PM2.5, ozone, and other pollutant data along possible paths. Green paths on the map indicate routes that maintain the highest Neural Index. Red zones should be avoided if possible - they have elevated pollution that may impact cognitive function.`;

    case 'map_help':
      return `The map shows your city with color-coded zones:\n• 🟢 Green areas = Safe (NI 80+)\n• 🟡 Yellow areas = Moderate (NI 60-79)\n• 🔴 Red areas = Avoid (NI <60)\n\nStreets show their Neural Index scores. Use the search bar to find destinations, and I'll route you through the safest areas. Controls: +/- to zoom, arrows to rotate, ⊙ to recenter.`;

    case 'recommendation':
      if (ctx.neuralIndex >= 80) {
        return `Conditions are excellent! Now is a great time for:\n• Focused work or study\n• Outdoor exercise\n• Important meetings\n\nYour Neural Index of ${ctx.neuralIndex} supports peak cognitive performance.`;
      } else if (ctx.neuralIndex >= 60) {
        return `Conditions are moderate. I recommend:\n• Light activities are fine\n• Take breaks every 45 minutes\n• Stay hydrated\n• Consider moving to a greener area for important tasks\n\nCurrent NI: ${ctx.neuralIndex}`;
      } else {
        return `Environmental conditions are challenging (NI: ${ctx.neuralIndex}). I recommend:\n• Move to an indoor filtered environment\n• Postpone intensive cognitive tasks if possible\n• Use the navigation to find nearby parks or clean zones\n• Molecular Permission: ${ctx.molecularPermission}`;
      }

    case 'outdoor_advice':
      const outdoorOk = ctx.neuralIndex >= 70 && ctx.molecularPermission !== 'RESTRICTED';
      return outdoorOk
        ? `Yes, conditions are suitable for outdoor activities! Neural Index: ${ctx.neuralIndex}. Best areas are marked in green on the map. Would you like me to find a nearby park?`
        : `Current conditions suggest limiting outdoor exposure. NI: ${ctx.neuralIndex}, Permission: ${ctx.molecularPermission}. If you must go out, stick to the green-marked routes and avoid industrial zones.`;

    case 'forecast':
      return `Based on current trends, air quality is expected to ${ctx.neuralIndex >= 80 ? 'remain stable' : 'gradually improve'} over the next 2 hours. Check the Smog Micro-Forecast chart on the main screen for detailed predictions. I'll alert you when conditions change significantly.`;

    case 'about':
      return `I'm Blurp.ie, your cognitive navigation assistant. I help you:\n\n• Monitor real-time air quality and its impact on your brain\n• Calculate routes that maximize your Neural Index\n• Provide environmental intelligence and recommendations\n\nThink of me as a GPS for your cognitive wellbeing. What would you like to know?`;

    case 'thanks':
      return `You're welcome! Remember, I'm here to help you navigate for optimal cognitive performance. Your Neural Index is currently ${ctx.neuralIndex}. Let me know if you need anything else!`;

    case 'general':
    default:
      // Give contextual response based on current state
      if (ctx.isNavigating && ctx.destination) {
        // If navigating, assume questions might be about the journey
        return `You're navigating to ${ctx.destination}. ${formatDistance(ctx.routeDistance ?? 0)} remaining (about ${formatDuration(ctx.routeDuration ?? 0)}). Average Neural Index along this route: ${ctx.routeAverageNI ?? ctx.neuralIndex}.\n\nI can help you with:\n• "How long until I arrive?"\n• "How far is it?"\n• "What's the air quality?"\n• "Should I take a break?"`;
      }
      if (ctx.destination) {
        // Destination selected but not navigating
        return `You've selected ${ctx.destination}. Tap "Start Walk" to begin! I can tell you about:\n• Walking time and distance\n• Air quality along the route\n• Why this route was chosen`;
      }
      if (ctx.neuralIndex < 60) {
        return `I notice your Neural Index is low (${ctx.neuralIndex}). I'd recommend finding a cleaner area. Would you like me to help you find a park or green space nearby?`;
      }
      // General helpful response
      return `I'm Blurp.ie, your cognitive navigation assistant! I can help you with:\n\n• 🗺️ "How do I get to [place]?" - Navigate safely\n• ⏱️ "How long to reach?" - Get time/distance\n• 🌬️ "What's the air quality?" - Check pollution\n• 🧠 "What's my Neural Index?" - Cognitive health\n• 💡 "Should I go outside?" - Get recommendations\n\nYour current Neural Index: ${ctx.neuralIndex}. What would you like to know?`;
  }
}

/**
 * Get best running times summary for AI context
 */
function getBestTimeSummary(): string {
  const times = useRunningStore.getState().bestRunningTimes;
  if (times.length === 0) return 'No forecast data available yet.';

  const best = times.reduce((a, b) => a.recommendation_score > b.recommendation_score ? a : b);
  const excellent = times.filter(t => t.recommendation === 'excellent');
  const avoid = times.filter(t => t.recommendation === 'avoid');

  const formatH = (s: string) => { const h = parseInt(s); return h < 12 ? `${h}AM` : h === 12 ? '12PM' : `${h-12}PM`; };

  let summary = `Best time: ${formatH(best.time_slot)} (AQI ${best.predicted_aqi}, ${best.temperature}°C, UV ${best.uv_index}). `;
  if (excellent.length > 0) summary += `Optimal windows: ${excellent.map(t => formatH(t.time_slot)).join(', ')}. `;
  if (avoid.length > 0) summary += `Avoid: ${avoid.map(t => formatH(t.time_slot)).join(', ')} (high AQI/heat). `;
  return summary;
}

/**
 * Call the /api/chat serverless function (secure — API key stays server-side)
 */
async function callServerChat(userMessage: string): Promise<string> {
  try {
    const ctx = getContext();

    // Ensure best running times are loaded for "best time" questions
    const runStore = useRunningStore.getState();
    if (runStore.bestRunningTimes.length === 0 && ctx.currentLocation) {
      runStore.fetchBestRunningTimes(ctx.currentLocation.lat, ctx.currentLocation.lng);
    }

    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userMessage,
        routeState: {
          purpose: null,
          purposeRaw: null,
          startLocation: ctx.currentLocation ? `${ctx.currentLocation.lat.toFixed(4)}, ${ctx.currentLocation.lng.toFixed(4)}` : null,
          destination: ctx.destination,
          loopDistance: 0,
          preferences: [],
          concerns: [],
          phase: ctx.isNavigating ? 'navigating' : ctx.destination ? 'destination_selected' : 'idle',
          missing: [],
          aqi: ctx.aqi,
          neuralIndex: ctx.neuralIndex,
          pm25: ctx.pm25,
          bestRunningTimes: getBestTimeSummary(),
        },
        conversationHistory: [],
      }),
    });

    if (!response.ok) {
      console.error('Chat API error:', response.status);
      return generateResponse(userMessage);
    }

    const data = await response.json();
    return data.message || generateResponse(userMessage);
  } catch (error) {
    console.error('Chat API call failed:', error);
    return generateResponse(userMessage);
  }
}

/**
 * Process user message and return AI response
 */
export async function processMessage(userMessage: string): Promise<string> {
  // Check if user wants the full day forecast view
  const wantsFullDay = /\b(full (day|forecast|condition|schedule)|entire day|whole day|all hours|hourly forecast|show.*(forecast|schedule|breakdown)|24.hour)\b/i.test(userMessage);
  if (wantsFullDay) {
    emitChatAction('open-best-time');
    return "Opening the full 24-hour forecast for you — you can see every hour's AQI, temperature, humidity, and UV.";
  }

  // Try the serverless API first (uses server-side OpenAI key)
  try {
    const response = await callServerChat(userMessage);

    // If AI response suggests viewing the full forecast, also open it
    if (/\b(check the (full|Best Time)|view the forecast|see the full breakdown)\b/i.test(response)) {
      emitChatAction('open-best-time');
    }

    return response;
  } catch {
    // Fall back to rule-based
    await new Promise((resolve) => setTimeout(resolve, 300 + Math.random() * 300));
    return generateResponse(userMessage);
  }
}

export default { processMessage, generateResponse };
